import { BusinessObjectBase } from './base';
import type { MyUserTask } from './my-user-task';
import type { RoleAssignment } from './role-assignment';
import type { UserTask } from './user-task';

export interface RoleData {
  readonly id: number;
  readonly name?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly roleAssignments?: RoleAssignment[];
  readonly userTasks?: UserTask[];
  readonly myUserTasks?: MyUserTask[];
}

export interface Role extends Readonly<RoleData> {}
export class Role extends BusinessObjectBase {
  static readonly entityName = 'Role' as const;
  static readonly plural = 'roles';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Role[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Role(data));
  }

  static async findOne(filter?: any): Promise<Role | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Role(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<Role> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Role(data);
  }

  static async create(data: Omit<RoleData, 'id'>): Promise<Role> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Role(result);
  }

  static async updateById(id: number, data: Partial<RoleData>): Promise<Role> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new Role(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
